// v5 — inline error handling (no alert())
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { uploadPhoto } from '../lib/storage';
import { sendNotification, getAdminIds, getAdminEmails, getBranchManagerEmails, sendEmail, emailInspectionDiscrepancy } from '../lib/notify';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';

export default function MobileInspection({ onClose, onSuccess }) {
  const { profile, isAdmin } = useAuth();

  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(profile?.station_truck || '');
  const [selectedDepartment, setSelectedDepartment] = useState(profile?.department || '');
  const [stationsLoading, setStationsLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(profile?.branch_id || '');

  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [results, setResults] = useState({});
  const [step, setStep] = useState('station');
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const submitMsg = useMessage(); // shown on summary screen if submit fails

  const photoInputRefs = useRef({});

  const loadStations = useCallback(async () => {
    setStationsLoading(true);
    let q = supabase
      .from('tools')
      .select('station_truck, department, branch_id, branches(name)')
      .eq('status', 'active')
      .not('station_truck', 'is', null);
    if (selectedBranch) q = q.eq('branch_id', selectedBranch);
    const { data } = await q;
    const unique = [...new Map(
      (data || []).map(t => [`${t.branch_id}|${t.department}|${t.station_truck}`, t])
    ).values()].sort((a, b) => {
      const an = parseInt(a.station_truck);
      const bn = parseInt(b.station_truck);
      return isNaN(an) || isNaN(bn) ? a.station_truck.localeCompare(b.station_truck) : an - bn;
    });
    setStations(unique);
    setStationsLoading(false);
  }, [selectedBranch, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      supabase.from('branches').select('*').order('name').then(({ data }) => setBranches(data || []));
    }
  }, [isAdmin]);

  useEffect(() => { loadStations(); }, [loadStations]);

  const loadTools = useCallback(async (station, department) => {
    if (!station) return;
    setToolsLoading(true);
    let q = supabase
      .from('tools')
      .select('id, name, serial_number, model_number, department')
      .eq('status', 'active')
      .eq('station_truck', station)
      .order('name');
    if (profile?.branch_id) q = q.eq('branch_id', profile.branch_id);
    if (department) q = q.eq('department', department);
    const { data } = await q;
    setTools(data || []);
    const init = {};
    (data || []).forEach(t => {
      init[t.id] = { passed: null, note: '', showNote: false, photoFile: null, photoPreview: null };
    });
    setResults(init);
    setToolsLoading(false);
  }, [profile?.branch_id]);

  function selectStation(station, department) {
    setSelectedStation(station);
    setSelectedDepartment(department);
    loadTools(station, department);
    setStep('checklist');
  }

  function setResult(toolId, passed) {
    setResults(prev => ({ ...prev, [toolId]: { ...prev[toolId], passed, showNote: !passed } }));
  }

  function setNote(toolId, note) {
    setResults(prev => ({ ...prev, [toolId]: { ...prev[toolId], note } }));
  }

  function handlePhotoChange(toolId, file) {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setResults(prev => ({ ...prev, [toolId]: { ...prev[toolId], photoFile: file, photoPreview: preview } }));
  }

  function clearPhoto(toolId) {
    setResults(prev => ({ ...prev, [toolId]: { ...prev[toolId], photoFile: null, photoPreview: null } }));
    if (photoInputRefs.current[toolId]) photoInputRefs.current[toolId].value = '';
  }

  const allChecked = tools.every(t => results[t.id]?.passed !== null);
  const passCount = tools.filter(t => results[t.id]?.passed === true).length;
  const failCount = tools.filter(t => results[t.id]?.passed === false).length;
  const unchecked = tools.filter(t => results[t.id]?.passed === null).length;

  async function submitInspection() {
    setSaving(true);
    submitMsg.clearMessage();
    try {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const matchingTool = tools[0];
      const eventUuid = crypto.randomUUID();

      setUploadingPhotos(true);
      const photoPathMap = {};
      for (const tool of tools) {
        const result = results[tool.id];
        if (result?.passed === false && result?.photoFile) {
          const { path, error } = await uploadPhoto(result.photoFile, 'inspections');
          if (!error && path) photoPathMap[tool.id] = path;
        }
      }
      setUploadingPhotos(false);

      const { data: inspEvent, error: inspError } = await supabase
        .from('inspections')
        .insert({
          branch_id: parseInt(profile?.branch_id),
          station_truck: selectedStation,
          department: matchingTool?.department || null,
          inspection_date: dateStr,
          conducted_by: profile?.id || null,
          conducted_by_name: profile?.full_name,
          status: 'completed',
          total_tools_checked: tools.length,
          discrepancies_found: failCount,
          notes: failCount > 0
            ? tools.filter(t => results[t.id]?.passed === false).map(t => `${t.name}: ${results[t.id]?.note || 'Issue noted'}`).join('; ')
            : null,
          completed_at: now.toISOString(),
          event_uuid: eventUuid,
        })
        .select()
        .single();

      if (inspError) throw inspError;

      const items = tools.map(t => ({
        inspection_id: eventUuid,
        tool_id: null,
        tool_name: t.name,
        serial_number: t.serial_number || null,
        status: results[t.id]?.passed ? 'pass' : 'fail',
        discrepancy_note: results[t.id]?.passed === false ? (results[t.id]?.note || null) : null,
        photo_url: photoPathMap[t.id] || null,
        checked_at: now.toISOString(),
      }));

      const { error: itemsError } = await supabase.from('inspection_items').insert(items);
      if (itemsError) throw itemsError;

      if (failCount > 0) {
        const adminIds = await getAdminIds();
        await sendNotification(adminIds, `Inspection discrepancy — ${selectedStation}`, `${profile?.full_name} logged ${failCount} discrepancy(s) on ${selectedStation}. Review inspection.`, 'inspection_discrepancy', 'tools', 'inspect');
        const adminEmails = await getAdminEmails();
        const managerEmails = await getBranchManagerEmails(profile?.branch_id);
        const allEmails = [...new Set([...adminEmails, ...managerEmails])];
        const discrepancyText = tools.filter(t => results[t.id]?.passed === false).map(t => `${t.name}${results[t.id]?.note ? ': ' + results[t.id].note : ''}`).join(', ');
        await sendEmail(allEmails, `Inspection discrepancy — ${selectedStation}`, emailInspectionDiscrepancy(selectedStation, profile?.branch_name || 'Branch', failCount, discrepancyText, profile?.full_name));
      }

      onSuccess?.();
    } catch (err) {
      submitMsg.showError('Something went wrong: ' + err.message);
    } finally {
      setSaving(false);
      setUploadingPhotos(false);
    }
  }

  // ── STATION PICKER ──
  if (step === 'station') {
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#1565C0', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: 'white', padding: '6px 10px', fontSize: 18, cursor: 'pointer' }}>←</button>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>Log inspection</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Select station or truck</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {isAdmin && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Select branch</div>
              <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E0E0', fontFamily: 'Barlow, sans-serif', background: 'white' }}>
                <option value="">All branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          {stationsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>Loading stations...</div>
          ) : stations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>No active stations found for your branch.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {profile?.station_truck && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Your station</div>
                  <button onClick={() => selectStation(profile.station_truck, profile.department)} style={{ width: '100%', padding: '18px 20px', borderRadius: 12, border: '2px solid #1565C0', background: '#E3F2FD', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>📍</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: '#1565C0' }}>{profile.station_truck}</div>
                      {profile?.department && <div style={{ fontSize: 13, color: '#5C5C5C' }}>{profile.department}</div>}
                    </div>
                    <span style={{ marginLeft: 'auto', color: '#1565C0', fontSize: 20 }}>→</span>
                  </button>
                </div>
              )}
              {stations.filter(s => s.station_truck !== profile?.station_truck).length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>All stations</div>
                  {stations.filter(s => s.station_truck !== profile?.station_truck).map(s => (
                    <button key={`${s.branch_id}|${s.department}|${s.station_truck}`} onClick={() => selectStation(s.station_truck, s.department)} style={{ width: '100%', padding: '16px 20px', borderRadius: 12, border: '2px solid #E0E0E0', background: 'white', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 20 }}>🚛</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#0F0F0F' }}>{s.station_truck}</div>
                        {s.department && <div style={{ fontSize: 13, color: '#5C5C5C' }}>{s.department}</div>}
                      </div>
                      <span style={{ marginLeft: 'auto', color: '#D0D0D0', fontSize: 18 }}>→</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // ── CHECKLIST ──
  if (step === 'checklist') {
    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#1565C0', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => setStep('station')} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: 'white', padding: '6px 10px', fontSize: 18, cursor: 'pointer' }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>{selectedStation}</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              {allChecked ? `${passCount} passed · ${failCount} issues` : `${tools.length - unchecked} of ${tools.length} checked`}
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>{tools.length - unchecked}/{tools.length}</div>
        </div>
        <div style={{ height: 4, background: 'rgba(21,101,192,0.15)' }}>
          <div style={{ height: '100%', background: '#39D353', width: `${tools.length > 0 ? ((tools.length - unchecked) / tools.length) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {toolsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>Loading tools...</div>
          ) : tools.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9A9A9A' }}>No active tools found for this station.</div>
          ) : (
            tools.map(tool => {
              const result = results[tool.id];
              const passed = result?.passed;
              return (
                <div key={tool.id} style={{ borderBottom: '1px solid #F0F0F0', background: passed === true ? '#F1FFF3' : passed === false ? '#FFF5F5' : 'white', transition: 'background 0.2s' }}>
                  <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#0F0F0F' }}>{tool.name}</div>
                      {tool.serial_number && <div style={{ fontSize: 12, color: '#9A9A9A' }}>S/N: {tool.serial_number}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => setResult(tool.id, true)} style={{ width: 52, height: 44, borderRadius: 10, border: 'none', fontSize: 20, cursor: 'pointer', fontWeight: 700, background: passed === true ? '#2E7D32' : '#E8F5E9', color: passed === true ? 'white' : '#2E7D32', transition: 'all 0.15s' }}>✓</button>
                      <button onClick={() => setResult(tool.id, false)} style={{ width: 52, height: 44, borderRadius: 10, border: 'none', fontSize: 20, cursor: 'pointer', fontWeight: 700, background: passed === false ? '#C62828' : '#FFEBEE', color: passed === false ? 'white' : '#C62828', transition: 'all 0.15s' }}>✗</button>
                    </div>
                  </div>
                  {result?.showNote && (
                    <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <input type="text" placeholder="Describe the issue..." value={result.note} onChange={e => setNote(tool.id, e.target.value)} style={{ width: '100%', padding: '10px 14px', border: '2px solid #FFCDD2', borderRadius: 8, fontFamily: 'Barlow, sans-serif', background: '#FFF5F5', boxSizing: 'border-box' }} autoFocus />
                      {!result.photoPreview ? (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: '2px dashed #FFCDD2', background: '#FFF5F5', cursor: 'pointer', fontSize: 13, color: '#C62828', fontWeight: 600 }}>
                            📷 Camera
                            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handlePhotoChange(tool.id, e.target.files?.[0])} />
                          </label>
                          <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: '2px dashed #FFCDD2', background: '#FFF5F5', cursor: 'pointer', fontSize: 13, color: '#C62828', fontWeight: 600 }}>
                            📁 Photo
                            <input ref={el => photoInputRefs.current[tool.id] = el} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handlePhotoChange(tool.id, e.target.files?.[0])} />
                          </label>
                        </div>
                      ) : (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={result.photoPreview} alt="Issue photo" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, border: '2px solid #FFCDD2', display: 'block' }} />
                          <button onClick={() => clearPhoto(tool.id)} style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #E0E0E0', background: 'white', position: 'sticky', bottom: 0 }}>
          {!allChecked && <div style={{ textAlign: 'center', fontSize: 13, color: '#9A9A9A', marginBottom: 10 }}>{unchecked} tool{unchecked !== 1 ? 's' : ''} remaining</div>}
          <button onClick={() => setStep('summary')} disabled={!allChecked} style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', fontSize: 16, fontWeight: 700, cursor: allChecked ? 'pointer' : 'not-allowed', background: allChecked ? '#1565C0' : '#E0E0E0', color: allChecked ? 'white' : '#9A9A9A', transition: 'all 0.2s' }}>
            {allChecked ? 'Review & submit →' : 'Check all tools to continue'}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  // ── SUMMARY ──
  if (step === 'summary') {
    const failedTools = tools.filter(t => results[t.id]?.passed === false);
    const passedTools = tools.filter(t => results[t.id]?.passed === true);

    return createPortal(
      <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: failCount > 0 ? '#C62828' : '#2E7D32', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setStep('checklist')} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, color: 'white', padding: '6px 10px', fontSize: 18, cursor: 'pointer' }}>←</button>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>Inspection summary</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{selectedStation}</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: '16px', textAlign: 'center', border: '2px solid #C8E6C9' }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#2E7D32' }}>{passCount}</div>
              <div style={{ fontSize: 13, color: '#2E7D32', fontWeight: 600 }}>Passed</div>
            </div>
            <div style={{ background: failCount > 0 ? '#FFEBEE' : '#F5F5F5', borderRadius: 12, padding: '16px', textAlign: 'center', border: `2px solid ${failCount > 0 ? '#FFCDD2' : '#E0E0E0'}` }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: failCount > 0 ? '#C62828' : '#9A9A9A' }}>{failCount}</div>
              <div style={{ fontSize: 13, color: failCount > 0 ? '#C62828' : '#9A9A9A', fontWeight: 600 }}>Issues</div>
            </div>
          </div>
          {failedTools.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#C62828', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Issues found</div>
              {failedTools.map(t => (
                <div key={t.id} style={{ padding: '12px 16px', borderRadius: 10, marginBottom: 8, background: '#FFF5F5', border: '1px solid #FFCDD2' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#C62828' }}>✗ {t.name}</div>
                  {results[t.id]?.note && <div style={{ fontSize: 13, color: '#5C5C5C', marginTop: 4, fontStyle: 'italic' }}>"{results[t.id].note}"</div>}
                  {results[t.id]?.photoPreview && <img src={results[t.id].photoPreview} alt="Issue" style={{ marginTop: 8, width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #FFCDD2' }} />}
                </div>
              ))}
            </div>
          )}
          {passedTools.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Passed</div>
              {passedTools.map(t => (
                <div key={t.id} style={{ padding: '10px 16px', borderRadius: 10, marginBottom: 6, background: '#F1FFF3', border: '1px solid #C8E6C9', fontSize: 14, color: '#2E7D32', fontWeight: 500 }}>✓ {t.name}</div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #E0E0E0', background: 'white' }}>
          {failCount > 0 && (
            <div style={{ background: '#FFF3E0', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#9A5F00', marginBottom: 12, border: '1px solid #FFE0B2' }}>
              ⚠️ {failCount} issue{failCount !== 1 ? 's' : ''} will be reported to your manager.
              {failedTools.some(t => results[t.id]?.photoFile) && <span> Photos will be uploaded with the report.</span>}
            </div>
          )}
          {/* Inline error shown here if submit fails */}
          <InlineMessage type={submitMsg.type} message={submitMsg.message} onDismiss={submitMsg.clearMessage} style={{ marginBottom: 12 }} />
          <button onClick={submitInspection} disabled={saving} style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', fontSize: 16, fontWeight: 700, background: saving ? '#E0E0E0' : (failCount > 0 ? '#C62828' : '#2E7D32'), color: saving ? '#9A9A9A' : 'white', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {uploadingPhotos ? 'Uploading photos...' : saving ? 'Submitting...' : '✓ Submit inspection'}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return null;
}